import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, useTheme, Alert } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Question, QuestionOption } from '../../types';
import { DragIndicator as DragIcon } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface Props {
  question: Question;
  selectedOptions?: number[];
  onChange: (orderedOptionIds: number[]) => void;
}

type ColumnId = 'left' | 'middle' | 'right';

interface ColumnItem {
  id: number;
  content: string;
  correctColumn: 'left' | 'right';
  isCorrectlyPlaced?: boolean;
}

interface ColumnState {
  left: ColumnItem[];
  middle: ColumnItem[];
  right: ColumnItem[];
}

export const HorizontalDragDropQuestion: React.FC<Props> = ({
  question,
  selectedOptions = [],
  onChange,
}) => {
  const theme = useTheme();
  
  // State to track items in each column
  const [columns, setColumns] = useState<ColumnState>({
    left: [],
    middle: [],
    right: []
  });

  // State to track validation errors
  const [validationErrors, setValidationErrors] = useState<{[key: number]: string}>({});
  
  // State to track if all items are correctly placed
  const [allCorrect, setAllCorrect] = useState<boolean>(false);

  // Initialize the columns when the question changes
  useEffect(() => {
    if (question.options && question.options.length > 0) {
      // Start with all options in the middle column
      const items: ColumnItem[] = question.options.map(option => ({
        id: option.id,
        content: option.front_text || '',
        correctColumn: (option.back_text?.toLowerCase() === 'right') ? 'right' : 'left'
      }));
      
      setColumns({
        left: [],
        middle: items,
        right: []
      });
      
      // Reset validation errors
      setValidationErrors({});
      setAllCorrect(false);
    }
  }, [question.options]);

  // Update the selected options whenever the columns change
  useEffect(() => {
    const newSelectedOptions: number[] = [];
    let allItemsCorrect = true;
    const newValidationErrors: {[key: number]: string} = {};
    
    // Check left column items
    columns.left.forEach(item => {
      newSelectedOptions.push(item.id);
      const isCorrect = item.correctColumn === 'left';
      if (!isCorrect) {
        allItemsCorrect = false;
        newValidationErrors[item.id] = 'This item belongs in the right column';
      }
    });
    
    // Check right column items
    columns.right.forEach(item => {
      newSelectedOptions.push(item.id);
      const isCorrect = item.correctColumn === 'right';
      if (!isCorrect) {
        allItemsCorrect = false;
        newValidationErrors[item.id] = 'This item belongs in the left column';
      }
    });
    
    // If there are still items in the middle, not all items are correctly placed
    if (columns.middle.length > 0) {
      allItemsCorrect = false;
    }
    
    // Update validation state
    setValidationErrors(newValidationErrors);
    setAllCorrect(allItemsCorrect && columns.middle.length === 0 && 
                 (columns.left.length > 0 || columns.right.length > 0));
    
    // Only update selected options if all items are correctly placed
    if (allItemsCorrect && columns.middle.length === 0) {
      onChange(newSelectedOptions);
    } else {
      // If not all correct, send empty array to indicate invalid state
      onChange([]);
    }
  }, [columns, onChange]);

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    
    // If dropped outside a droppable area
    if (!destination) return;
    
    // If dropped in the same place
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    
    // Get source and destination column IDs
    const sourceColId = source.droppableId as ColumnId;
    const destColId = destination.droppableId as ColumnId;
    
    // Create a copy of the current columns state
    const newColumns = { ...columns };
    
    // Remove the item from the source column
    const [movedItem] = newColumns[sourceColId].splice(source.index, 1);
    
    // Check if the item is being placed in the correct column
    if (destColId !== 'middle') {
      movedItem.isCorrectlyPlaced = movedItem.correctColumn === destColId;
    } else {
      // If moving back to middle, remove the correctness flag
      delete movedItem.isCorrectlyPlaced;
    }
    
    // Add the item to the destination column
    newColumns[destColId].splice(destination.index, 0, movedItem);
    
    // Update the state
    setColumns(newColumns);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          gap: 2,
          mb: 2
        }}>
          {/* Left Column */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Typography 
              variant="h6" 
              align="center" 
              sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}
            >
              {question.low_text || 'Left Column'}
            </Typography>
            <Droppable droppableId="left">
              {(provided, snapshot) => (
                <Paper
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    width: '100%',
                    minHeight: 200,
                    p: 2,
                    bgcolor: snapshot.isDraggingOver ? 'rgba(66, 165, 245, 0.1)' : 'background.paper',
                    border: '1px dashed',
                    borderColor: snapshot.isDraggingOver ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  {columns.left.map((item, index) => (
                    <Draggable key={item.id} draggableId={`item-${item.id}`} index={index}>
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            p: 2,
                            bgcolor: item.isCorrectlyPlaced === false ? 'error.light' : 
                                   (snapshot.isDragging ? 'action.hover' : 'background.default'),
                            borderRadius: 1,
                            boxShadow: snapshot.isDragging ? 2 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                            border: item.isCorrectlyPlaced === false ? '1px solid' : 'none',
                            borderColor: 'error.main'
                          }}
                        >
                          <DragIcon sx={{ color: 'text.secondary' }} />
                          <Typography sx={{ flex: 1 }}>{item.content}</Typography>
                          {item.isCorrectlyPlaced === false && (
                            <ErrorIcon color="error" />
                          )}
                          {item.isCorrectlyPlaced === true && (
                            <CheckCircleIcon color="success" />
                          )}
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Paper>
              )}
            </Droppable>
          </Box>
          
          {/* Middle Column */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Typography 
              variant="h6" 
              align="center" 
              sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}
            >
              Drag Items
            </Typography>
            <Droppable droppableId="middle">
              {(provided, snapshot) => (
                <Paper
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    width: '100%',
                    minHeight: 200,
                    p: 2,
                    bgcolor: snapshot.isDraggingOver ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                    border: '1px dashed',
                    borderColor: snapshot.isDraggingOver ? 'text.secondary' : 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  {columns.middle.map((item, index) => (
                    <Draggable key={item.id} draggableId={`item-${item.id}`} index={index}>
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            p: 2,
                            bgcolor: snapshot.isDragging ? 'action.hover' : 'background.default',
                            borderRadius: 1,
                            boxShadow: snapshot.isDragging ? 2 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' }
                          }}
                        >
                          <DragIcon sx={{ color: 'text.secondary' }} />
                          <Typography>{item.content}</Typography>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Paper>
              )}
            </Droppable>
          </Box>
          
          {/* Right Column */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Typography 
              variant="h6" 
              align="center" 
              sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}
            >
              {question.high_text || 'Right Column'}
            </Typography>
            <Droppable droppableId="right">
              {(provided, snapshot) => (
                <Paper
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    width: '100%',
                    minHeight: 200,
                    p: 2,
                    bgcolor: snapshot.isDraggingOver ? 'rgba(244, 67, 54, 0.1)' : 'background.paper',
                    border: '1px dashed',
                    borderColor: snapshot.isDraggingOver ? 'secondary.main' : 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  {columns.right.map((item, index) => (
                    <Draggable key={item.id} draggableId={`item-${item.id}`} index={index}>
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            p: 2,
                            bgcolor: item.isCorrectlyPlaced === false ? 'error.light' : 
                                   (snapshot.isDragging ? 'action.hover' : 'background.default'),
                            borderRadius: 1,
                            boxShadow: snapshot.isDragging ? 2 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                            border: item.isCorrectlyPlaced === false ? '1px solid' : 'none',
                            borderColor: 'error.main'
                          }}
                        >
                          <DragIcon sx={{ color: 'text.secondary' }} />
                          <Typography sx={{ flex: 1 }}>{item.content}</Typography>
                          {item.isCorrectlyPlaced === false && (
                            <ErrorIcon color="error" />
                          )}
                          {item.isCorrectlyPlaced === true && (
                            <CheckCircleIcon color="success" />
                          )}
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Paper>
              )}
            </Droppable>
          </Box>
        </Box>
        
        {/* Validation message */}
        {Object.keys(validationErrors).length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Some items are in the wrong column. Please move them to the correct column.
          </Alert>
        )}
        
        {allCorrect && columns.middle.length === 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            All items are correctly placed!
          </Alert>
        )}
        
        {columns.middle.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please drag all items from the middle to either the left or right column.
          </Alert>
        )}
      </DragDropContext>
    </Box>
  );
};
